import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid } from "@mui/x-data-grid";
import { Button, CircularProgress } from "@mui/material";
import VisibilityIcon from "@material-ui/icons/Visibility";
import useDocFormsStore from "../../store/docFormsStore";
import { useAssignedForms } from "../../hooks/useAssignedForms";
import PDFViewerModal from "../PDFViewerModal/PDFViewerModal";
import FormsListEmptyState from "../FormsListEmptyState/FormsListEmptyState";
import MagicLinkInboxBanner, { quietDataGridSx } from "../MagicLinkInboxBanner/MagicLinkInboxBanner";
import {
	isMagicLinkSession,
	getMagicLinkHighlightFormRequestId,
	getMagicLinkHighlightedFormName,
	getMagicLinkViewPath,
} from "Services/docForm.service";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles(() => ({
	container: {
		backgroundColor: "#fff",
		padding: "14px 16px",
		height: "100%",
		flex: 1,
		display: "flex",
		flexDirection: "column",
	},
	docCell: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		lineHeight: 1.3,
		padding: "4px 0",
	},
	docName: {
		fontWeight: 500,
		fontSize: 14,
		color: "#111827",
	},
	docSub: {
		fontSize: 12,
		color: "#6b7280",
		marginTop: 2,
	},
	senderCell: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		lineHeight: 1.3,
		padding: "4px 0",
	},
	senderName: {
		fontSize: 14,
		fontWeight: 500,
		color: "#111827",
	},
	senderEmail: {
		fontSize: 12,
		color: "#6b7280",
		marginTop: 2,
	},
	viewBtn: {
		textTransform: "none",
		fontSize: 13,
		padding: "4px 14px",
		borderRadius: 6,
	},
}));

export default function CompletedView({ setLoading }) {
	const classes = useStyles();
	const navigate = useNavigate();
	const [selectedPdf, setSelectedPdf] = useState(null);
	const [completedLoading, setCompletedLoading] = useState(true);

	const completedForms = useDocFormsStore((state) => state.completedForms);
	const { getAssignedFormsFilteredByCompleted } = useAssignedForms();
	const highlightId = getMagicLinkHighlightFormRequestId();
	const isMagicLink = isMagicLinkSession();

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			setCompletedLoading(true);
			await getAssignedFormsFilteredByCompleted();
			if (!cancelled) setCompletedLoading(false);
		};
		load();
		return () => {
			cancelled = true;
		};
	}, []);

	const rows = useMemo(
		() =>
			(completedForms || []).map((form, index) => ({
				...form,
				id: form.id || `completed-${index}`,
			})),
		[completedForms]
	);

	const highlightedName = getMagicLinkHighlightedFormName(rows);
	const viewPath = getMagicLinkViewPath();

	const openRow = (row) => {
		if (row.templateId && row.id) {
			navigate(`/docFormFiller/${row.templateId}/${row.id}`);
			return;
		}
		if (row.pdfUrl) {
			setSelectedPdf(row.pdfUrl);
		}
	};

	const columns = [
		{
			field: "templateName",
			headerName: "Document",
			flex: 2,
			renderCell: (params) => {
				const { templateName, pageCount } = params.row;
				const sub = pageCount ? `${pageCount} page${pageCount !== 1 ? "s" : ""}` : null;
				return (
					<div className={classes.docCell}>
						<span className={classes.docName}>{templateName || "—"}</span>
						{sub && <span className={classes.docSub}>{sub}</span>}
					</div>
				);
			},
		},
		{
			field: "sender",
			headerName: "Sender",
			flex: 1.5,
			renderCell: (params) => {
				const { senderName, senderEmail } = params.row;
				if (!senderName && !senderEmail) return <span style={{ color: "#9ca3af" }}>—</span>;
				return (
					<div className={classes.senderCell}>
						{senderName && <span className={classes.senderName}>{senderName}</span>}
						{senderEmail && <span className={classes.senderEmail}>{senderEmail}</span>}
					</div>
				);
			},
		},
		{
			field: "requestedAt",
			headerName: "Requested",
			flex: 1,
			valueGetter: (params) =>
				params.row.requestedAtDisplay || params.row.requestedAt || "—",
		},
		{
			field: "submittedAt",
			headerName: "Submitted",
			flex: 1,
			valueGetter: (params) =>
				params.row.completedAtDisplay || params.row.completedAt || "—",
		},
		{
			field: "actions",
			headerName: "Actions",
			flex: 0.8,
			sortable: false,
			renderCell: (params) => {
				const isHighlighted =
					highlightId && String(params.row.id) === String(highlightId);
				return (
					<Button
						variant={isHighlighted ? "contained" : "text"}
						size="small"
						className={classes.viewBtn}
						startIcon={<VisibilityIcon style={{ fontSize: 15 }} />}
						onClick={() => openRow(params.row)}
						sx={isHighlighted
							? { backgroundColor: "#006ac6", color: "#fff", "&:hover": { backgroundColor: "#0056a3" } }
							: { color: "#006ac6" }
						}
					>
						View
					</Button>
				);
			},
		},
	];

	if (completedLoading) {
		return (
			<div
				className={classes.container}
				style={{ alignItems: "center", justifyContent: "center" }}
			>
				<CircularProgress color="primary" />
			</div>
		);
	}

	if (!rows.length) {
		return <FormsListEmptyState variant="completed" />;
	}

	return (
		<div className={classes.container}>
			{isMagicLink && highlightId && rows.some((r) => String(r.id) === String(highlightId)) && (
				<MagicLinkInboxBanner
					formName={highlightedName || "Your form"}
					onOpenForm={viewPath ? () => navigate(viewPath) : undefined}
					actionLabel="View your submission"
					tab="Completed"
				/>
			)}
			<DataGrid
				rows={rows}
				columns={columns}
				pageSize={10}
				rowsPerPageOptions={[10]}
				disableSelectionOnClick
				autoHeight
				rowHeight={56}
				getRowClassName={(params) =>
					highlightId && String(params.id) === String(highlightId)
						? "magic-link-highlight-row"
						: ""
				}
				sx={quietDataGridSx}
			/>
			<PDFViewerModal
				open={!!selectedPdf}
				onClose={() => setSelectedPdf(null)}
				pdfUrl={selectedPdf}
			/>
		</div>
	);
}
